import { Campaign, defaultUser, User, UserHelper, UserInCampaign } from '@hub/shared'
import { HTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './campaign-status-bar.component.component.scss'

const NUMBER_OF_PROFILES_TO_SHOW = 3

interface Props extends HTMLAttributes<HTMLDivElement> {
  campaign: Campaign
  users: UserInCampaign
  type?: string
}

export default ({ className = '', style = {}, id = '', campaign, users, type }: Props) => {
  const [t] = useTranslation('campaignStatusBar')
  const userHelper = useMemo(() => new UserHelper(), [])
  const [userObject, setUserObject] = useState<User | null>(null)

  useEffect(() => {
    userHelper.getUser().then((u) => setUserObject(u))
  }, [userHelper])

  const parseStatus = useCallback(() => {
    if (users?.recruited?.length) {
      if (['finished', 'archived'].includes(campaign?.status)) {
        return 'participated'
      }
      return 'recruited'
    }
    if (!users?.hasVacancies && type !== 'sent') {
      return 'noVacancies'
    }
    if (users?.invited?.length) {
      return 'invited'
    }
    if (users?.refuseds?.length) {
      return 'refuseds'
    }
    if (users?.interested?.length) {
      return 'interested'
    }

    if (campaign?.activation?.suggestion && !campaign?.activation?.suggestionVisible && users.fit < 101) {
      return 'notVisible'
    }
    if (userObject && !userObject[campaign.socialNetworks[0]]?.length) {
      return 'notSocialNetwork'
    }
    if (users?.hasProfile?.length) {
      return 'hasProfile'
    }
    if (users?.inContact?.length) {
      return 'inContact'
    }
    return 'notProfile'
  }, [users, campaign, userObject, type])

  const parseObjectToLoad = useCallback(() => {
    if (parseStatus() === 'noVacancies') {
      return 'hasProfile'
    }
    return parseStatus()
  }, [parseStatus])

  const parseBadgeColor = useCallback(() => {
    switch (parseStatus()) {
      case 'inContact':
        return 'blue-dark'
      case 'interested':
        return 'gold'
      case 'noVacancies':
        return 'red'
      case 'refuseds':
        return 'red'
      default:
        return 'green'
    }
  }, [parseStatus])

  return (
    <div className={`campaign-status-bar ${className}`} style={style} id={id}>
      <p dangerouslySetInnerHTML={{ __html: t(parseStatus()) }} />
      {!['notProfile', 'noVacancies'].includes(parseStatus()) &&
        (users[parseObjectToLoad()]?.length ? (
          <ul className="photos">
            {users[parseObjectToLoad()]?.map((user, i: number) =>
              i < 3 ? (
                <li key={user?.idProfile || user?.id} className={`${users[parseObjectToLoad()].length > 1 ? `position-${i + 1}` : ''}`}>
                  <figure>
                    {['interested', 'noVacancies', 'refuseds', 'inContact', 'recruited'].includes(parseStatus()) &&
                      users[parseObjectToLoad()]?.length === i + 1 && <span className={`badge background-${parseBadgeColor()}`}></span>}
                    <img
                      src={`https://storage.googleapis.com/squid-profile-pictures/${user?.idProfile || user?.id}.jpg`}
                      alt={user?.username}
                      title={user?.username}
                      className="img-fluid"
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null
                        currentTarget.src = defaultUser
                      }}
                    />
                  </figure>
                </li>
              ) : null
            )}
            {users[parseObjectToLoad()]?.length > NUMBER_OF_PROFILES_TO_SHOW ? (
              <li key={'more-users'} className="more">
                +{users[parseObjectToLoad()].length - NUMBER_OF_PROFILES_TO_SHOW}
              </li>
            ) : null}
          </ul>
        ) : null)}
    </div>
  )
}
